import { isDefined, isEmpty, isNotDefined } from '@sgme/fp';
import { Checkbox } from '@sgme/ui';
import deepEqual from 'fast-deep-equal';
import { useIntl } from 'react-intl';

import { selectCurrencyPairData } from '@/api/config/selectors.ts';
import { userApi } from '@/api/user/index.ts';
import { useCheckCashByTradeCapture } from '@/App/tiles/CashTile/CashContext.tsx';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import { useAppSelector } from '@/store/hooks.ts';

type NDFCheckboxProps = {
  tileId: string;
};

// business rules: fx-web/app/src/components/FxTileHeader/connect/connectNDCheckbox.ts
// UI: fx-web/app/src/components/FxTileHeader/NDCheckbox.tsx
export const NDFCheckbox = (props: NDFCheckboxProps) => {
  const { tileId } = props;

  const { formatMessage } = useIntl();

  const checkCashByTradeCapture = useCheckCashByTradeCapture();

  const { type, currencyPair, tradeCapture, isNdf, isVisible, isEditable } = useAppSelector((state) => {
    const tile = selectCashTile(state, tileId);

    const localMarketCurrencies = userApi.endpoints.currentUser.select()(state).data?.localMarketsCcy ?? [];

    if (isNotDefined(tile)) {
      return {};
    }

    const currencyPairData = selectCurrencyPairData(state, tile.properties.currencyPair);

    // in FX, if one of the currencies is in the local market, the tile is editable
    // event if currencyPairData?.canBeDelivered is false
    // fx-web/app/src/state/referenceData/referenceDataReducer.ts -> updateCcyPairsWithLocalMarket
    const isEditable =
      (currencyPairData?.canBeDelivered ?? false) ||
      (isDefined(tile.properties.currency1) && localMarketCurrencies.includes(tile.properties.currency1)) ||
      (isDefined(tile.properties.currency2) && localMarketCurrencies.includes(tile.properties.currency2));

    // see forex/app/src/api/config/getCurrencies.ts
    // for the business rules

    const canDealNonDeliverable =
      userApi.endpoints.currentUser.select()(state).data?.productsAccess.nonDeliverable ?? false;

    return {
      type: tile.type,
      currencyPair: tile.properties.currencyPair,
      tradeCapture: tile.tradeCapture,
      isNdf: tile.type === 'FxNdf',

      // in FX
      // extCcyPair !== null &&
      //   canDealND &&
      //   (extCcyPair.notDeliverable || extCcyPair.isCashDeliverable),
      isVisible:
        canDealNonDeliverable && isDefined(currencyPairData)
          ? // ? (currencyPairData.notDeliverable || currencyPairData.canBeDelivered) && !currencyPairData.isInverted
            currencyPairData.notDeliverable || currencyPairData.isCashDeliverable
          : false,

      isEditable,
    };
  }, deepEqual);

  if (isNotDefined(type) || isEmpty(currencyPair) || !isVisible) {
    return null;
  }

  const handleToggleNdf = () => {
    if (isNotDefined(tradeCapture)) {
      return;
    }

    checkCashByTradeCapture({
      isNonDeliverable: !isNdf,
    });
  };

  return (
    <Checkbox
      label={formatMessage({ id: 'CashTile.NDFCheckbox.label' })}
      checked={isNdf}
      disabled={!isEditable}
      onClick={handleToggleNdf}
      dataE2e="ndf-checkbox"
    />
  );
};
